import { Badge } from "@/components/Badge";
import { useProfileLabel } from "@/lib/profileLabels";
import type { Mode } from "@ranking-arena/shared/types";

interface HeaderProps {
  profileA: string;
  profileB: string;
  mode: Mode;
  lastVoteAt: string | null;
  lastIndexVersion: string | null;
}

/**
 * Page header for Results: "A vs B" title, mode + dev-data badges,
 * and a last-vote / index-version stamp on the right.
 */
export function Header({ profileA, profileB, mode, lastVoteAt, lastIndexVersion }: HeaderProps) {
  const label = useProfileLabel();
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--atf-pink-700)",
            }}
          >
            {label(profileA)}
          </span>
          <span
            style={{
              color: "var(--muted-foreground)",
              fontSize: 18,
              fontWeight: 400,
            }}
          >
            vs
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--atf-blue-950)",
            }}
          >
            {label(profileB)}
          </span>
        </h1>
        <Badge variant="outline">{mode}</Badge>
        <Badge variant="warn">dev data</Badge>
        <div
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "var(--muted-foreground)",
            fontFamily: "var(--font-mono)",
            textAlign: "right",
          }}
        >
          last vote {timeAgo(lastVoteAt)}
          <br />
          index {lastIndexVersion ?? "—"}
        </div>
      </div>
      <p
        style={{
          margin: "8px 0 0",
          color: "var(--muted-foreground)",
          fontSize: 13,
          maxWidth: 680,
        }}
      >
        Aggregate is order-independent — swapping A and B in the sidebar flips the presentation, not
        the numbers.
      </p>
    </div>
  );
}

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch {
    return iso;
  }
}
